import React, { useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { mockParserApi } from '../../services/mockParserApi';

const EXAM_OPTIONS = ['CUET'];
const PAPER_OPTIONS = {
  CUET: [
    'General Test',
    'English',
    'Mathematics',
    'Physics',
    'Chemistry',
    'Biology'
  ],
};

const TEST_MODES = [
  {
    value: 'pyq',
    label: 'PYQ Only',
    description: '100% Previous Year Questions',
    color: 'blue',
  },
  {
    value: 'ai-generated',
    label: 'AI Generated',
    description: '100% AI-generated questions',
    color: 'purple',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    description: '50% PYQ + 50% AI',
    color: 'indigo',
  },
];

export default function MockTestGenerator({ onGenerateSuccess }) {
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState({
    mode: 'mixed',
    exam: 'CUET',
    paper: 'General Test',
    questionCount: 50,
    duration: 60,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: value,
      // Reset paper when exam changes
      ...(name === 'exam' && { paper: PAPER_OPTIONS[value][0] }),
    }));
  };

  const handleModeChange = (mode) => {
    setConfig((prev) => ({ ...prev, mode }));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    const generateToast = toast.loading('Generating mock test...');

    try {
      const result = await mockParserApi.generateMockTest({
        mode: config.mode,
        exam: config.exam,
        paper: config.paper,
        questionCount: parseInt(config.questionCount, 10),
        duration: parseInt(config.duration, 10),
      });

      if (result.success) {
        toast.success(
          `Mock test generated successfully! ID: ${result.data?.testId?.substring(0, 8)}...`,
          { id: generateToast, duration: 5000 }
        );

        // Notify parent component
        if (onGenerateSuccess) {
          onGenerateSuccess(result.data);
        }
      } else {
        toast.error(result.error || 'Failed to generate mock test', {
          id: generateToast,
        });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate mock test', {
        id: generateToast,
      });
      console.error('Generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center mb-4">
        <SparklesIcon className="h-6 w-6 text-purple-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-900">
          Generate Mock Test
        </h3>
      </div>

      <div className="space-y-4">
        {/* Test Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Test Mode
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TEST_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                onClick={() => handleModeChange(mode.value)}
                disabled={generating}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  config.mode === mode.value
                    ? `border-${mode.color}-600 bg-${mode.color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="font-semibold text-gray-900 mb-1">
                  {mode.label}
                </div>
                <div className="text-xs text-gray-600">{mode.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Exam and Paper Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="exam"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exam
            </label>
            <select
              id="exam"
              name="exam"
              value={config.exam}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={generating}
            >
              {EXAM_OPTIONS.map((exam) => (
                <option key={exam} value={exam}>
                  {exam}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="paper"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Paper Type
            </label>
            <select
              id="paper"
              name="paper"
              value={config.paper}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={generating}
            >
              {PAPER_OPTIONS[config.exam].map((paper) => (
                <option key={paper} value={paper}>
                  {paper}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Question Count and Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="questionCount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Number of Questions
            </label>
            <input
              type="number"
              id="questionCount"
              name="questionCount"
              value={config.questionCount}
              onChange={handleInputChange}
              min="10"
              max="200"
              step="5"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={generating}
            />
          </div>

          <div>
            <label
              htmlFor="duration"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Duration (minutes)
            </label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={config.duration}
              onChange={handleInputChange}
              min="15"
              max="180"
              step="5"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={generating}
            />
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all font-medium"
        >
          {generating ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Generating Test...
            </span>
          ) : (
            'Generate Mock Test'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          * AI generation may take 30-90 seconds depending on the test mode
        </p>
      </div>
    </div>
  );
}
